import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, HelpCircle, Pencil, Plus, Star, Trash2 } from "lucide-react";

import { adminCmsApi } from "@profitlens/api-client";
import type {
  FaqItemOut,
  HelpDocOut,
  LandingContentSave,
  LandingFeature,
  PricingCardOut,
} from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent, CardHeader } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@profitlens/ui/tabs";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const textareaClass =
  "min-h-24 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50";

const LOCALES = ["zh-CN", "en-US"] as const;

/** 官网管理：Landing 文案 / 定价卡片 / FAQ / 帮助文档，全部改完即生效，不用等发版。 */
export function WebsitePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("landing");

  return (
    <div className="space-y-4">
      <PageHeader title={t("website.title")} subtitle={t("website.subtitle")} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="landing">{t("website.tabLanding")}</TabsTrigger>
          <TabsTrigger value="pricing">{t("website.tabPricing")}</TabsTrigger>
          <TabsTrigger value="faq">{t("website.tabFaq")}</TabsTrigger>
          <TabsTrigger value="help">{t("website.tabHelp")}</TabsTrigger>
        </TabsList>

        <TabsContent value="landing">
          <LandingEditor />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingCardEditor />
        </TabsContent>
        <TabsContent value="faq">
          <FaqEditor />
        </TabsContent>
        <TabsContent value="help">
          <HelpDocEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------- Landing 文案

/** 编辑态：features 在表单里始终是数组，customized 只用于提示徽标。 */
type LandingDraft = Omit<LandingContentSave, "features"> & {
  features: LandingFeature[];
  customized: boolean;
};

function LandingEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState<string>("zh-CN");
  const [draft, setDraft] = useState<LandingDraft | null>(null);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const query = useQuery({
    queryKey: ["admin", "cms", "landing"],
    queryFn: () => adminCmsApi.getLanding(),
  });

  useEffect(() => {
    const found = query.data?.find((row) => row.locale === locale);
    if (!found) return;
    setFeedback(null);
    setDraft({
      hero_title: found.hero_title,
      hero_subtitle: found.hero_subtitle,
      primary_cta_label: found.primary_cta_label,
      secondary_cta_label: found.secondary_cta_label,
      features: found.features,
      pricing_title: found.pricing_title,
      pricing_subtitle: found.pricing_subtitle,
      footer_note: found.footer_note,
      seo_title: found.seo_title,
      seo_description: found.seo_description,
      customized: found.customized,
    });
  }, [query.data, locale]);

  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("no draft");
      return adminCmsApi.saveLanding(locale, {
        hero_title: draft.hero_title,
        hero_subtitle: draft.hero_subtitle,
        primary_cta_label: draft.primary_cta_label,
        secondary_cta_label: draft.secondary_cta_label,
        features: draft.features,
        pricing_title: draft.pricing_title,
        pricing_subtitle: draft.pricing_subtitle,
        footer_note: draft.footer_note,
        seo_title: draft.seo_title,
        seo_description: draft.seo_description,
      });
    },
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("common.saved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "landing"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) => setFeedback({ kind: "err", text: translateError(err, t) }),
  });

  if (query.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }

  const patch = (next: Partial<LandingContentSave>) =>
    setDraft((prev) => (prev ? { ...prev, ...next } : prev));

  const setFeature = (index: number, next: Partial<LandingFeature>) =>
    setDraft((prev) => {
      if (!prev) return prev;
      const features = prev.features.map((item, i) => (i === index ? { ...item, ...next } : item));
      return { ...prev, features };
    });

  const addFeature = () =>
    setDraft((prev) =>
      prev ? { ...prev, features: [...prev.features, { title: "", description: "" }] } : prev,
    );

  const removeFeature = (index: number) =>
    setDraft((prev) =>
      prev ? { ...prev, features: prev.features.filter((_, i) => i !== index) } : prev,
    );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1.5">
              <Label>{t("website.locale")}</Label>
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCALES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code === "zh-CN" ? t("settings.localeZh") : t("settings.localeEn")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {draft ? (
              draft.customized ? (
                <Badge variant="success">{t("website.customized")}</Badge>
              ) : (
                <Badge variant="warning">{t("website.usingDefault")}</Badge>
              )
            ) : null}
          </div>

          {!draft ? (
            <p className="text-sm text-muted">{t("common.loading")}</p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t("website.heroTitle")}</Label>
                  <Input
                    value={draft.hero_title}
                    onChange={(e) => patch({ hero_title: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t("website.heroSubtitle")}</Label>
                  <textarea
                    className={textareaClass}
                    value={draft.hero_subtitle ?? ""}
                    onChange={(e) => patch({ hero_subtitle: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("website.primaryCta")}</Label>
                  <Input
                    value={draft.primary_cta_label ?? ""}
                    onChange={(e) => patch({ primary_cta_label: e.target.value })}
                    maxLength={60}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("website.secondaryCta")}</Label>
                  <Input
                    value={draft.secondary_cta_label ?? ""}
                    onChange={(e) => patch({ secondary_cta_label: e.target.value })}
                    maxLength={60}
                  />
                </div>
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <Label>{t("website.features")}</Label>
                  <Button size="sm" variant="outline" onClick={addFeature}>
                    <Plus className="h-3.5 w-3.5" />
                    {t("website.featureAdd")}
                  </Button>
                </div>
                <p className="text-xs text-muted">{t("website.featuresHint")}</p>
                {draft.features.length === 0 ? (
                  <p className="text-sm text-muted">{t("website.featuresEmpty")}</p>
                ) : (
                  <div className="space-y-3">
                    {draft.features.map((feature, index) => (
                      <div
                        key={index}
                        className="grid gap-3 rounded-lg border border-border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"
                      >
                        <div className="space-y-1.5">
                          <Label>{t("website.featureTitle")}</Label>
                          <Input
                            value={feature.title}
                            onChange={(e) => setFeature(index, { title: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>{t("website.featureDescription")}</Label>
                          <Input
                            value={feature.description}
                            onChange={(e) => setFeature(index, { description: e.target.value })}
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFeature(index)}
                            aria-label={t("common.remove")}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-danger" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("website.pricingTitle")}</Label>
                  <Input
                    value={draft.pricing_title ?? ""}
                    onChange={(e) => patch({ pricing_title: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("website.footerNote")}</Label>
                  <Input
                    value={draft.footer_note ?? ""}
                    onChange={(e) => patch({ footer_note: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{t("website.pricingSubtitle")}</Label>
                  <Input
                    value={draft.pricing_subtitle ?? ""}
                    onChange={(e) => patch({ pricing_subtitle: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("website.seoTitle")}</Label>
                  <Input
                    value={draft.seo_title ?? ""}
                    onChange={(e) => patch({ seo_title: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("website.seoDescription")}</Label>
                  <Input
                    value={draft.seo_description ?? ""}
                    onChange={(e) => patch({ seo_description: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 border-t border-border pt-4">
                <Button
                  onClick={() => save.mutate()}
                  disabled={save.isPending || !draft.hero_title.trim()}
                >
                  {save.isPending ? t("common.saving") : t("common.save")}
                </Button>
                {feedback ? (
                  <p
                    className={
                      feedback.kind === "ok" ? "text-xs text-success" : "text-xs text-danger"
                    }
                  >
                    {feedback.text}
                  </p>
                ) : null}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- 定价卡片

interface PricingRow {
  code: string;
  display_name: string;
  card_description: string;
  featuresText: string;
  is_highlighted: boolean;
}

function toRow(card: PricingCardOut): PricingRow {
  return {
    code: card.code,
    display_name: card.display_name ?? "",
    card_description: card.card_description ?? "",
    featuresText: card.card_features.join("\n"),
    is_highlighted: card.is_highlighted,
  };
}

function PricingCardEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState<string>("zh-CN");
  const [rows, setRows] = useState<PricingRow[]>([]);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const query = useQuery({
    queryKey: ["admin", "cms", "pricing-cards", locale],
    queryFn: () => adminCmsApi.getPricingCards({ locale }),
  });

  useEffect(() => {
    if (query.data) setRows(query.data.map(toRow));
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      adminCmsApi.putPricingCards(
        {
          items: rows.map((row) => ({
            code: row.code,
            display_name: row.display_name.trim() || null,
            card_description: row.card_description.trim() || null,
            card_features: row.featuresText
              .split(/[\n,]/)
              .map((item) => item.trim())
              .filter(Boolean),
            is_highlighted: row.is_highlighted,
          })),
        },
        { locale },
      ),
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("common.saved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "pricing-cards"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) => setFeedback({ kind: "err", text: translateError(err, t) }),
  });

  if (query.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }

  const patchRow = (code: string, next: Partial<PricingRow>) =>
    setRows((prev) => prev.map((row) => (row.code === code ? { ...row, ...next } : row)));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="p-5 pb-0">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="text-sm text-muted">{t("website.pricingHint")}</p>
            <div className="space-y-1.5">
              <Label>{t("website.locale")}</Label>
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCALES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code === "zh-CN" ? t("settings.localeZh") : t("settings.localeEn")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted">{t("website.pricingHintShared")}</p>
        </CardHeader>
        <CardContent className="space-y-4 p-5">
          {rows.length === 0 ? (
            <p className="text-sm text-muted">{t("website.pricingEmpty")}</p>
          ) : (
            <div className="space-y-4">
              {rows.map((row) => {
                const meta = query.data?.find((card) => card.code === row.code);
                return (
                  <div key={row.code} className="space-y-3 rounded-lg border border-border p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold">{row.code}</p>
                      {meta ? (
                        <>
                          <Badge variant="secondary">{meta.plan}</Badge>
                          <span className="text-xs text-muted">
                            {meta.amount} {meta.currency} / {meta.days}
                            {t("website.daysSuffix")}
                          </span>
                        </>
                      ) : null}
                      {!meta?.enabled ? (
                        <Badge variant="secondary">{t("common.disabled")}</Badge>
                      ) : null}
                      {meta && !meta.customized && locale !== "zh-CN" ? (
                        <Badge variant="warning">{t("website.cardLocaleFallback")}</Badge>
                      ) : null}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>{t("website.cardDisplayName")}</Label>
                        <Input
                          value={row.display_name}
                          onChange={(e) => patchRow(row.code, { display_name: e.target.value })}
                          placeholder={row.code}
                        />
                      </div>
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 text-sm">
                          <Switch
                            checked={row.is_highlighted}
                            onCheckedChange={(v) => patchRow(row.code, { is_highlighted: v })}
                          />
                          {t("website.cardHighlight")}
                        </label>
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>{t("website.cardDescription")}</Label>
                        <Input
                          value={row.card_description}
                          onChange={(e) => patchRow(row.code, { card_description: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>{t("website.cardFeatures")}</Label>
                        <textarea
                          className={textareaClass}
                          value={row.featuresText}
                          onChange={(e) => patchRow(row.code, { featuresText: e.target.value })}
                        />
                        <p className="text-xs text-muted">{t("website.cardFeaturesHint")}</p>
                      </div>
                    </div>
                    {row.is_highlighted ? (
                      <p className="flex items-center gap-1 text-xs text-brand">
                        <Star className="h-3.5 w-3.5" />
                        {t("website.cardHighlightOn")}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-3 border-t border-border pt-4">
            <Button onClick={() => save.mutate()} disabled={save.isPending || rows.length === 0}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {feedback ? (
              <p
                className={feedback.kind === "ok" ? "text-xs text-success" : "text-xs text-danger"}
              >
                {feedback.text}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- FAQ

function FaqEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState("zh-CN");
  const [editing, setEditing] = useState<FaqItemOut | "new" | null>(null);

  const query = useQuery({
    queryKey: ["admin", "cms", "faq", locale],
    queryFn: () => adminCmsApi.listFaq({ locale }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminCmsApi.deleteFaq(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "faq"] });
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-5">
          <div className="space-y-1.5">
            <Label>{t("website.locale")}</Label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code === "zh-CN" ? t("settings.localeZh") : t("settings.localeEn")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            {t("website.faqAdd")}
          </Button>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data && query.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("website.faqEmpty")}</Card>
      ) : (
        <div className="space-y-3">
          {query.data?.map((item) => (
            <Card key={item.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 p-4 pb-2">
                <div className="flex min-w-0 items-center gap-2">
                  <HelpCircle className="h-4 w-4 shrink-0 text-brand" />
                  <p className="truncate text-sm font-semibold">{item.question}</p>
                  <Badge variant="secondary">
                    {t("website.sortOrder")}
                    {item.sort_order}
                  </Badge>
                  {item.enabled ? (
                    <Badge variant="success">{t("common.enabled")}</Badge>
                  ) : (
                    <Badge variant="secondary">{t("common.disabled")}</Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEditing(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                    {t("common.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(t("common.confirmDelete"))) remove.mutate(item.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="text-sm whitespace-pre-wrap text-muted">{item.answer}</p>
                {item.updated_at ? (
                  <p className="mt-2 text-xs text-muted">
                    {t("common.updatedAt")} · {formatDateTime(item.updated_at)}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FaqDialog
        locale={locale}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "faq"] });
        }}
      />
    </div>
  );
}

function FaqDialog({
  locale,
  editing,
  onClose,
  onSaved,
}: {
  locale: string;
  editing: FaqItemOut | "new" | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const isNew = editing === "new";
  const existing = editing !== null && editing !== "new" ? editing : null;

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setQuestion(existing.question);
      setAnswer(existing.answer);
      setSortOrder(String(existing.sort_order));
      setEnabled(existing.enabled);
    } else {
      setQuestion("");
      setAnswer("");
      setSortOrder("0");
      setEnabled(true);
    }
    setError(null);
  }, [editing, existing]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        question,
        answer,
        sort_order: Math.max(0, Number.parseInt(sortOrder, 10) || 0),
        enabled,
      };
      if (isNew) return adminCmsApi.createFaq({ locale, ...body });
      if (!existing) throw new Error("no faq");
      return adminCmsApi.patchFaq(existing.id, body);
    },
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? t("website.faqAdd") : t("website.faqEdit")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("website.faqQuestion")}</Label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("website.faqAnswer")}</Label>
            <textarea
              className={textareaClass}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("website.sortOrder")}</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={enabled} onCheckedChange={setEnabled} />
                {t("common.enable")}
              </label>
            </div>
          </div>
          <p className="text-xs text-muted">{t("website.faqSortHint")}</p>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={save.isPending || !question.trim()} onClick={() => save.mutate()}>
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 帮助文档

function HelpDocEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState("zh-CN");
  const [editing, setEditing] = useState<HelpDocOut | "new" | null>(null);

  const query = useQuery({
    queryKey: ["admin", "cms", "help-docs", locale],
    queryFn: () => adminCmsApi.listHelpDocs({ locale }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminCmsApi.deleteHelpDoc(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "help-docs"] });
    },
  });

  const togglePublished = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      adminCmsApi.patchHelpDoc(id, { published }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "help-docs"] });
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-5">
          <div className="space-y-1.5">
            <Label>{t("website.locale")}</Label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCALES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code === "zh-CN" ? t("settings.localeZh") : t("settings.localeEn")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            {t("website.docAdd")}
          </Button>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data && query.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("website.docEmpty")}</Card>
      ) : (
        <div className="space-y-3">
          {query.data?.map((doc) => (
            <Card key={doc.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 p-4 pb-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-brand" />
                  <p className="truncate text-sm font-semibold">{doc.title}</p>
                  <Badge variant="secondary">{doc.slug}</Badge>
                  {doc.category ? <Badge variant="outline">{doc.category}</Badge> : null}
                  {doc.published ? (
                    <Badge variant="success">{t("website.docPublished")}</Badge>
                  ) : (
                    <Badge variant="warning">{t("website.docDraft")}</Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={togglePublished.isPending}
                    onClick={() =>
                      togglePublished.mutate({ id: doc.id, published: !doc.published })
                    }
                  >
                    {doc.published ? t("website.docUnpublish") : t("website.docPublish")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(doc)}>
                    <Pencil className="h-3.5 w-3.5" />
                    {t("common.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(t("common.confirmDelete"))) remove.mutate(doc.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {doc.summary ? <p className="text-sm text-muted">{doc.summary}</p> : null}
                {doc.updated_at ? (
                  <p className="mt-2 text-xs text-muted">
                    {t("common.updatedAt")} · {formatDateTime(doc.updated_at)}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <HelpDocDialog
        locale={locale}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ["admin", "cms", "help-docs"] });
        }}
      />
    </div>
  );
}

function HelpDocDialog({
  locale,
  editing,
  onClose,
  onSaved,
}: {
  locale: string;
  editing: HelpDocOut | "new" | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const isNew = editing === "new";
  const existing = editing !== null && editing !== "new" ? editing : null;

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("");
  const [body, setBody] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [published, setPublished] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setSlug(existing.slug);
      setTitle(existing.title);
      setSummary(existing.summary);
      setCategory(existing.category);
      setBody(existing.body);
      setSortOrder(String(existing.sort_order));
      setPublished(existing.published);
    } else {
      setSlug("");
      setTitle("");
      setSummary("");
      setCategory("");
      setBody("");
      setSortOrder("0");
      setPublished(true);
    }
    setError(null);
  }, [editing, existing]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        slug,
        title,
        summary,
        category,
        body,
        sort_order: Math.max(0, Number.parseInt(sortOrder, 10) || 0),
        published,
      };
      if (isNew) return adminCmsApi.createHelpDoc({ locale, ...payload });
      if (!existing) throw new Error("no doc");
      return adminCmsApi.patchHelpDoc(existing.id, payload);
    },
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isNew ? t("website.docAdd") : t("website.docEdit")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("website.docTitle")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("website.docSlug")}</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="connect-store"
              />
              <p className="text-xs text-muted">{t("website.docSlugHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("website.docCategory")}</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("website.sortOrder")}</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("website.docSummary")}</Label>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("website.docBody")}</Label>
            <textarea
              className={`${textareaClass} min-h-48`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <p className="text-xs text-muted">{t("website.docBodyHint")}</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={published} onCheckedChange={setPublished} />
            {t("website.docPublished")}
          </label>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={save.isPending || !title.trim() || slug.trim().length < 2}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
